import {Component, computed, inject, OnInit, signal} from '@angular/core';
import {IconBtnComponent} from '../../icon-b/icon-btn.component';
import {Card} from "primeng/card";
import {
  FileUploadHandlerEvent,
} from 'primeng/fileupload';
import {CreateTaBaseComponent} from '../create-ta-base-component.directive';
import {ProgressBar} from 'primeng/progressbar';
import {FileTypeIconsComponent} from '../../file-type-icons/file-type-icons.component';
import {ReactiveFormsModule} from '@angular/forms';
import {TableModule} from 'primeng/table';
import {FileDetails} from '../../../../api';
import {UploadDocumentComponent} from '../upload-document/upload-document.component';
import {ConfirmationService} from 'primeng/api';
import {TaToastComponent} from '../../ta-toast/ta-toast.component';
import {PermissionService} from '../../../../services/permission.service';

@Component({
  selector: 'create-ta-documentation',
  imports: [
    IconBtnComponent,
    Card,
    ProgressBar,
    FileTypeIconsComponent,
    ReactiveFormsModule,
    TableModule,
    UploadDocumentComponent,
    TaToastComponent,
  ],
  templateUrl: './create-ta-documentation.component.html',
  styleUrl: './create-ta-documentation.component.scss'
})
export class CreateTaDocumentationComponent extends CreateTaBaseComponent implements OnInit {
  confirmationService = inject(ConfirmationService);
  permissionService = inject(PermissionService);

  uploadingDocuments = signal<{ id: number; name: string; size: number; progress: number }[]>([]);
  private uploadIdCounter = 0;
  uploadedDocuments = computed<FileDetails[]>(() => this.store.taDocuments());

  requiredDocumentTypes = computed<string[]>(() => {
    const currentPurpose = this.store.taSubmission()?.purpose;
    const travelReasonOptions = this.store.session().details?.travelReasonOptions ?? [];
    const matchingReason = travelReasonOptions.find(option => option.value === currentPurpose);
    return matchingReason?.documentHelp ?? [];
  });
  uploadDisabled = signal<boolean>(false);

  override ngOnInit() {
    super.ngOnInit();
    this.uploadDisabled.set(!this.permissionService.canUploadDocuments());
  }

  uploadFile(event: FileUploadHandlerEvent): void {
    const files: File[] = event.files ?? [];
    for (const file of files) {
      const id = ++this.uploadIdCounter;
      this.uploadingDocuments.update(docs => [...docs, { id, name: file.name, size: file.size, progress: 0 }]);

      this.taSubmissionDatasource.uploadTaDocument(file).subscribe({
        next: (progress) => {
          this.uploadingDocuments.update(docs =>
            docs.map(d => d.id === id ? { ...d, progress } : d)
          );
        },
        complete: () => {
          this.uploadingDocuments.update(docs => docs.filter(d => d.id !== id));
        },
        error: () => {
          this.uploadingDocuments.update(docs => docs.filter(d => d.id !== id));
        },
      });
    }
  }

  removeFile(file?: FileDetails): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to remove file ' + file?.fileName,
      header: 'Confirm Remove',
      icon: 'pi pi-info-circle',
      rejectLabel: 'Cancel',
      rejectButtonProps: {
        label: 'Cancel',
        severity: 'secondary',
        outlined: true,
      },
      acceptButtonProps: {
        label: 'Remove',
        severity: 'danger',
      },

      accept: () => {
        if (!!file?.fileId) {
          this.taSubmissionDatasource.removeTaDocument(file.fileId);
        } else {
          console.error('Can\'t find file id')
        }      },
      reject: () => {},
    });

  }

  downloadFile(fileId?: string): void {
    if (!!fileId) {
      const url = '/clcta/file/download/' + fileId;
      window.open(url, '_blank');
    } else {
      console.error('Can\'t find file ID.')
    }
  }

  getIconClass(documentType: string): string {
    return 'fa-light fa-file'
  }

  formatBytes(bytes: number, decimals = 2): string {
    if (bytes === 0) return '0 B';

    const k = 1000;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(decimals)) + ' ' + sizes[i];
  }

}
